import React, { useContext, useEffect, useState } from 'react'

import cx from 'classnames'
import { isEmpty, isNull, isUndefined } from 'lodash'
import { changeFilterGroups } from 'slices/filters'

import { Box, Button, Flex } from '@driftt/dds'
import { Div, Input, Label, Modal, Span } from '@driftt/tide-core'
import { access } from '@driftt/traffic-control'

import { DeserializedSavedFilter, INewSavedFilter } from 'api/AccountActivityAPI/types'
import FiltersForm from 'components/FiltersForm'
import Portal from 'components/Portal'
import { CurrentUserContext } from 'context/currentUserContext'
import { useAttributeFilters } from 'hooks/useAttributeFilters'
import useFilterConfiguration from 'hooks/useFilterConfiguration'
import { useAppDispatch } from 'hooks/useRedux'
import useSavedFilters from 'hooks/useSavedFilters'
import { FilterGroup, FilterPage } from 'utils/filters'
import { segmentTrack, TrackType } from 'utils/tracking'

import AdminFilterSettings from './AdminFilterSettings'
import { filterHasChanged } from './utils'

import styles from './styles.module.css'

const invalidFilterMessage = 'Enter a valid name for your filter'
interface SaveFilterModalProps {
  onClose: () => void
  editableGroups: FilterGroup[]
  savedFilter?: DeserializedSavedFilter
}

const SaveFilterModal: React.FC<SaveFilterModalProps> = ({
  editableGroups,
  savedFilter,
  onClose
}) => {
  const { defaultFilterId, createNewSavedFilter, updateSavedFilter } = useSavedFilters()
  const currentUser = useContext(CurrentUserContext)
  const [showFormValidation, setShowFormValidation] = useState(false)
  const attributeFilters = useAttributeFilters()
  const filterConfiguration = useFilterConfiguration(attributeFilters)
  const initialGroups = savedFilter ? savedFilter.body.filterGroups : editableGroups
  const [filterGroups, setFilterGroups] = useState(initialGroups)
  const [assignedTeams, setAssignedTeams] = useState<number[]>([])
  const [assignedUsers, setAssignedUsers] = useState<number[]>([])
  const dispatch = useAppDispatch()
  const isAdmin = access.isAdmin()
  const hasOrgSettingsEnabled = access.hasFeatures([
    { featureName: 'filter_admin_settings', accessLevel: 'WRITE' }
  ])

  const onChangeFilterGroups = (filterGroups: FilterGroup[], page: FilterPage) =>
    dispatch(changeFilterGroups({ filterGroups, page }))
  const [hasEmailNotifications, setHasEmailNotifications] = useState(false)
  const [hasDesktopNotifications, setHasDesktopNotifications] = useState(false)
  const [filterName, setFilterName] = useState<string>('')
  const [isDefault, setIsDefault] = useState<boolean>(false)
  const [isShared, setIsShared] = useState<boolean>(false)

  const isFilterNameValid = filterName && filterName !== ''
  const showFilterNameValidation = showFormValidation && !isFilterNameValid
  const hasOrgSettings = hasOrgSettingsEnabled && (assignedTeams.length || assignedUsers.length) > 0
  const changeFilterName = (e: any) => {
    setFilterName(e.target.value)
  }

  const changeIsDefault = (value: boolean) => {
    setIsDefault(value)
  }

  const changeIsShared = (value: boolean) => {
    setIsShared(value)
  }

  const updateFilter = async () => {
    if (!savedFilter || !currentUser) return
    const updatedFilter: DeserializedSavedFilter = {
      id: savedFilter.id,
      orgId: savedFilter.orgId,
      ownerId: savedFilter.ownerId,
      name: filterName,
      shared: isShared || hasOrgSettings,
      preloaded: savedFilter.preloaded,
      body: {
        descending: true,
        filterGroups: filterGroups,
        limit: 100,
        sortBy: 'MOST_RECENT'
      },
      updatedAt: new Date().getTime()
    }

    if (hasOrgSettings) {
      updatedFilter.orgSettings = {
        usersWithFilter: assignedUsers,
        teamsWithFilter: assignedTeams,
        digestNotificationsTurnedOn: hasEmailNotifications,
        pushNotificationsTurnedOn: hasDesktopNotifications
      }
    }

    await updateSavedFilter(updatedFilter, isDefault)

    if (hasOrgSettings) {
      segmentTrack(TrackType.ADMIN_MANAGED_FILTER_UPDATED, {
        orgId: currentUser?.orgId,
        filterId: savedFilter.id,
        hasEmailNotifications,
        hasDesktopNotifications
      })
    }
    onClose()
  }

  const saveNewFilter = async () => {
    if (!currentUser) return
    const newSavedFilter: INewSavedFilter = {
      orgId: currentUser.orgId,
      ownerId: currentUser.id,
      name: filterName,
      shared: isShared || hasOrgSettings,
      preloaded: false,
      body: {
        descending: true,
        filterGroups: filterGroups,
        limit: 100,
        sortBy: 'MOST_RECENT'
      },
      updatedAt: new Date().getTime()
    }

    if (hasOrgSettings) {
      newSavedFilter.orgSettings = {
        usersWithFilter: assignedUsers,
        teamsWithFilter: assignedTeams,
        digestNotificationsTurnedOn: hasEmailNotifications,
        pushNotificationsTurnedOn: hasDesktopNotifications
      }
    }

    await createNewSavedFilter(newSavedFilter, isDefault)

    if (hasOrgSettings) {
      segmentTrack(TrackType.ADMIN_MANAGED_FILTER_CREATED, {
        orgId: currentUser?.orgId,
        hasEmailNotifications,
        hasDesktopNotifications
      })
    }
    onClose()
  }

  const isValidFilterValues = () => {
    const validGroups = filterGroups.every((group: any) => {
      return (
        !isEmpty(group) &&
        group.every(({ values }: any) => {
          if (Array.isArray(values)) {
            return !isEmpty(values)
          }
          return !isNull(values) && !isUndefined(values)
        })
      )
    })

    return validGroups
  }

  const onSaveFilters = () => {
    setShowFormValidation(true)
    if (isFilterNameValid && isValidFilterValues()) {
      savedFilter ? updateFilter() : saveNewFilter()
      onChangeFilterGroups(filterGroups, FilterPage.ACTIVITIES)
    }
  }

  useEffect(() => {
    if (savedFilter) {
      setFilterName(savedFilter.name)
      setIsShared(savedFilter.shared)
    }
    if (defaultFilterId) {
      setIsDefault(defaultFilterId === savedFilter?.id)
    }
  }, [savedFilter, defaultFilterId])

  const getTitle = () => {
    if (savedFilter) {
      return 'Edit saved filter'
    }
    return 'Create saved filter'
  }

  const isSaveDisabled = (): boolean => {
    if (!filterName || !isValidFilterValues()) {
      return true
    }

    if (hasOrgSettingsEnabled) {
      const filterHasChanges = filterHasChanged({
        filterGroups,
        filterName,
        assignedTeams,
        assignedUsers,
        hasEmailNotifications,
        hasDesktopNotifications,
        savedFilter,
        isAdmin
      })

      return !filterHasChanges
    }

    return false
  }

  return (
    <Portal>
      <Modal
        open={true}
        title={getTitle()}
        onClose={onClose}
        size="large"
        footerCopy={
          <React.Fragment>
            <Button disabled={isSaveDisabled()} variant="primary" onClick={onSaveFilters}>
              Save
            </Button>
            <Button variant="tertiary" onClick={onClose}>
              Cancel
            </Button>
          </React.Fragment>
        }>
        <Div className={styles.EditSavedFiltersModalContainer}>
          <Flex
            direction="column"
            css={{
              marginBottom: hasOrgSettingsEnabled ? '$3' : 0
            }}
            gap={3}
            fullWidth>
            <Box
              fullWidth
              css={{
                '& .tide-label': {
                  display: 'block'
                }
              }}>
              <Input
                value={filterName}
                onChange={changeFilterName}
                className={cx(styles.EditSavedFiltersModalInput, {
                  [styles.errorInput]: showFilterNameValidation
                })}
                label="Name"
                placeholder="e.g. Priority accounts"
              />
            </Box>

            {hasOrgSettingsEnabled && (
              <AdminFilterSettings
                setAssignedUsers={setAssignedUsers}
                setAssignedTeams={setAssignedTeams}
                sharedWithAll={isShared}
                setSharedWithAll={setIsShared}
                isDefault={isDefault}
                assignedUsers={assignedUsers}
                assignedTeams={assignedTeams}
                setIsDefault={setIsDefault}
                hasEmailNotifications={hasEmailNotifications}
                setHasEmailNotifications={setHasEmailNotifications}
                hasDesktopNotifications={hasDesktopNotifications}
                savedFilter={savedFilter}
                setHasDesktopNotifications={setHasDesktopNotifications}
              />
            )}
          </Flex>

          {showFilterNameValidation && (
            <Span className={styles.redText}>{invalidFilterMessage}</Span>
          )}

          {!hasOrgSettingsEnabled && (
            <Div className={styles.EditSavedFiltersModalPreferences}>
              <Label>Preferences</Label>

              <Input
                onChange={changeIsDefault}
                className={styles.EditSavedFiltersModalCheckbox}
                label="Set as my default saved filter"
                type="checkbox"
                checked={isDefault}
              />

              <Input
                onChange={changeIsShared}
                className={styles.EditSavedFiltersModalCheckbox}
                label="Share saved filters with everyone in the organization"
                type="checkbox"
                checked={isShared}
              />
            </Div>
          )}

          <Label>Filters</Label>
          <FiltersForm
            initialGroups={filterGroups}
            calendarOpenDirection={'up'}
            filterConfiguration={filterConfiguration}
            showFormValidation={showFormValidation}
            onChangeFilterGroups={setFilterGroups}
          />
        </Div>
      </Modal>
    </Portal>
  )
}

export default SaveFilterModal
